import { useState, useEffect, useMemo } from 'react'
import styles from './styles.module.scss'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { FALLBACK_ORGANISATIONS, mergeOrganisations } from './organisations'

interface UserCreateFormProps {
    onUserCreated: () => void
    onSyncUsers: () => void
    isSyncing: boolean
    // Orgs already present on users loaded by the parent page. Unioned with
    // the backend list so every org that currently exists can be picked.
    existingOrganisations?: string[]
}

// AWS Cognito password requirements
const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
}

const SPECIAL_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?'

const generateCompliantPassword = (): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const numbers = '0123456789'
    const special = '!@#$%^&*'

    // Ensure at least one of each required type
    let password = ''
    password += uppercase[Math.floor(Math.random() * uppercase.length)]
    password += lowercase[Math.floor(Math.random() * lowercase.length)]
    password += numbers[Math.floor(Math.random() * numbers.length)]
    password += special[Math.floor(Math.random() * special.length)]

    // Fill remaining with random characters from all types
    const allChars = uppercase + lowercase + numbers + special
    for (let i = password.length; i < 12; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)]
    }

    // Shuffle the password
    return password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('')
}

const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    if (password.length < PASSWORD_REQUIREMENTS.minLength) {
        errors.push(`At least ${PASSWORD_REQUIREMENTS.minLength} characters`)
    }
    if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
        errors.push('At least one uppercase letter')
    }
    if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
        errors.push('At least one lowercase letter')
    }
    if (PASSWORD_REQUIREMENTS.requireNumbers && !/[0-9]/.test(password)) {
        errors.push('At least one number')
    }
    if (PASSWORD_REQUIREMENTS.requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
        errors.push('At least one special character')
    }

    return { valid: errors.length === 0, errors }
}

interface CreatedUser {
    email: string
    name: string
    organisation: string
    password: string
    admin: boolean
}

export const UserCreateForm = ({
    onUserCreated,
    onSyncUsers,
    isSyncing,
    existingOrganisations = [],
}: UserCreateFormProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [serverOrganisations, setServerOrganisations] = useState<string[]>([])
    const organisations = useMemo(
        () => mergeOrganisations(serverOrganisations, existingOrganisations),
        [serverOrganisations, existingOrganisations],
    )
    const [isNewOrg, setIsNewOrg] = useState(false)
    const [newOrgName, setNewOrgName] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        organisation: '',
        password: '',
        admin: false,
    })
    const [passwordValidation, setPasswordValidation] = useState<{ valid: boolean; errors: string[] }>({
        valid: false,
        errors: [],
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null)
    const [copiedField, setCopiedField] = useState<string | null>(null)

    useEffect(() => {
        loadOrganisations()
    }, [])

    const loadOrganisations = async () => {
        try {
            const response = await api({
                url: 'cognito-organisations',
                data: {},
            })
            if (response.data.success) {
                setServerOrganisations(response.data.organisations)
            } else {
                setServerOrganisations(FALLBACK_ORGANISATIONS)
            }
        } catch (error) {
            console.error('Failed to load organisations:', error)
            setServerOrganisations(FALLBACK_ORGANISATIONS)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target
        const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value

        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }))

        // Validate password when it changes
        if (name === 'password') {
            setPasswordValidation(validatePassword(value))
        }
    }

    const handleGeneratePassword = () => {
        const newPassword = generateCompliantPassword()
        setFormData((prev) => ({ ...prev, password: newPassword }))
        setPasswordValidation(validatePassword(newPassword))
        setShowPassword(true) // Show generated password so user can see it
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const organisation = isNewOrg ? newOrgName.trim() : formData.organisation

        if (!formData.email || !formData.name || !organisation || !formData.password) {
            Report.warning('Validation Error', 'Please fill in all required fields.', 'Okay')
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.email)) {
            Report.warning('Validation Error', 'Please enter a valid email address.', 'Okay')
            return
        }

        // Validate password
        const pwValidation = validatePassword(formData.password)
        if (!pwValidation.valid) {
            Report.warning('Password Requirements', 'Password must have: ' + pwValidation.errors.join(', '), 'Okay')
            return
        }

        setIsSubmitting(true)
        Loading.circle('Creating user...')

        try {
            const response = await api({
                url: 'cognito-users-create',
                data: {
                    email: formData.email,
                    name: formData.name,
                    organisation,
                    admin: formData.admin,
                    temporaryPassword: formData.password,
                },
            })

            Loading.remove()

            if (response.data.success) {
                // Store created user details for success modal
                setCreatedUser({
                    email: formData.email,
                    name: formData.name,
                    organisation,
                    password: formData.password,
                    admin: formData.admin,
                })
                setShowSuccessModal(true)

                // Reset form
                setFormData({
                    email: '',
                    name: '',
                    organisation: '',
                    password: '',
                    admin: false,
                })
                setPasswordValidation({ valid: false, errors: [] })
                setShowPassword(false)
                setIsNewOrg(false)
                setNewOrgName('')
                setIsOpen(false)
            } else {
                Report.failure('Creation Failed', response.data.error || 'Failed to create user.', 'Okay')
            }
        } catch (error: any) {
            Loading.remove()
            console.error('Error creating user:', error)
            const errorMessage = error.response?.data?.error || error.message || 'Failed to create user.'
            Report.failure('Creation Failed', errorMessage, 'Okay')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        setFormData({
            email: '',
            name: '',
            organisation: '',
            password: '',
            admin: false,
        })
        setPasswordValidation({ valid: false, errors: [] })
        setShowPassword(false)
        setIsNewOrg(false)
        setNewOrgName('')
        setIsOpen(false)
    }

    const handleCopyToClipboard = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedField(field)
            setTimeout(() => setCopiedField(null), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false)
        setCreatedUser(null)
        setCopiedField(null)
        onUserCreated()
    }

    return (
        <div className={styles.formCard}>
            <div className={styles.formHeader}>
                <h3>Accounts</h3>
                <div className={styles.headerButtons}>
                    <button onClick={onSyncUsers} className={styles.syncButton} disabled={isSyncing}>
                        {isSyncing ? 'Syncing...' : '↻ Sync to Database'}
                    </button>
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className={`${styles.toggleButton} ${isOpen ? styles.active : ''}`}
                    >
                        {isOpen ? '− Close Form' : '+ Create New User'}
                    </button>
                </div>
            </div>

            {isOpen && (
                <form onSubmit={handleSubmit} className={styles.createForm}>
                    <div className={styles.formGroup}>
                        <label htmlFor="email">
                            Email Address <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="user@example.com"
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="name">
                            Full Name <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="John Smith"
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="organisation">
                            Organisation <span className={styles.required}>*</span>
                        </label>
                        {isNewOrg ? (
                            <div className={styles.newOrgWrapper}>
                                <input
                                    type="text"
                                    id="newOrganisation"
                                    value={newOrgName}
                                    onChange={(e) => setNewOrgName(e.target.value)}
                                    placeholder="Enter new organisation name"
                                    required
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsNewOrg(false)
                                        setNewOrgName('')
                                    }}
                                    className={styles.switchOrgButton}
                                    disabled={isSubmitting}
                                >
                                    Select Existing
                                </button>
                            </div>
                        ) : (
                            <div className={styles.newOrgWrapper}>
                                <select
                                    id="organisation"
                                    name="organisation"
                                    value={formData.organisation}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select an organisation</option>
                                    {organisations.map((org) => (
                                        <option key={org} value={org}>
                                            {org}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsNewOrg(true)
                                        setFormData((prev) => ({ ...prev, organisation: '' }))
                                    }}
                                    className={styles.switchOrgButton}
                                    disabled={isSubmitting}
                                >
                                    + New
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="password">
                            Password <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.passwordWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter password"
                                required
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className={styles.passwordToggle}
                                disabled={isSubmitting}
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                            <button
                                type="button"
                                onClick={handleGeneratePassword}
                                className={styles.generateButton}
                                disabled={isSubmitting}
                            >
                                Generate
                            </button>
                        </div>
                        {formData.password && !passwordValidation.valid && (
                            <div className={styles.passwordRequirements}>
                                <span className={styles.requirementsLabel}>Missing:</span>
                                <ul>
                                    {passwordValidation.errors.map((error, idx) => (
                                        <li key={idx}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {formData.password && passwordValidation.valid && (
                            <span className={styles.passwordValid}>✓ Password meets all requirements</span>
                        )}
                    </div>

                    <div className={styles.formGroupCheckbox}>
                        <label>
                            <input
                                type="checkbox"
                                name="admin"
                                checked={formData.admin}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                            />
                            Admin User
                        </label>
                        <span className={styles.helpText}>Admin users have elevated permissions</span>
                    </div>

                    <p className={styles.formNote}>
                        The user will be required to change their password on first login.
                    </p>

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className={styles.cancelButton}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            )}

            {/* Success Modal */}
            {showSuccessModal && createdUser && (
                <div className={styles.modalOverlay} onClick={handleCloseSuccessModal}>
                    <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.successModalHeader}>
                            <div className={styles.successIcon}>✓</div>
                            <h3>User Created Successfully</h3>
                        </div>
                        <div className={styles.successModalBody}>
                            <p className={styles.successMessage}>
                                The user has been created. Please share the login credentials below with the user.
                            </p>

                            <div className={styles.credentialsBox}>
                                <div className={styles.credentialRow}>
                                    <span className={styles.credentialLabel}>Name</span>
                                    <span className={styles.credentialValue}>{createdUser.name}</span>
                                </div>

                                <div className={styles.credentialRow}>
                                    <span className={styles.credentialLabel}>Email</span>
                                    <div className={styles.credentialCopyable}>
                                        <span className={styles.credentialValue}>{createdUser.email}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleCopyToClipboard(createdUser.email, 'email')}
                                            className={styles.copyButton}
                                        >
                                            {copiedField === 'email' ? '✓ Copied' : 'Copy'}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.credentialRow}>
                                    <span className={styles.credentialLabel}>Password</span>
                                    <div className={styles.credentialCopyable}>
                                        <code className={styles.passwordDisplay}>{createdUser.password}</code>
                                        <button
                                            type="button"
                                            onClick={() => handleCopyToClipboard(createdUser.password, 'password')}
                                            className={styles.copyButton}
                                        >
                                            {copiedField === 'password' ? '✓ Copied' : 'Copy'}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.credentialRow}>
                                    <span className={styles.credentialLabel}>Organisation</span>
                                    <span className={styles.credentialValue}>
                                        <span className={styles.orgBadge}>{createdUser.organisation}</span>
                                    </span>
                                </div>

                                {createdUser.admin && (
                                    <div className={styles.credentialRow}>
                                        <span className={styles.credentialLabel}>Role</span>
                                        <span className={`${styles.badge} ${styles.confirmed}`}>Admin</span>
                                    </div>
                                )}
                            </div>

                            <p className={styles.noteText}>
                                The user will be required to change their password on first login.
                            </p>
                        </div>
                        <div className={styles.successModalActions}>
                            <button
                                type="button"
                                onClick={() => {
                                    const text = `Login Credentials\n\nEmail: ${createdUser.email}\nPassword: ${createdUser.password}\n\nPlease change your password after first login.`
                                    handleCopyToClipboard(text, 'all')
                                }}
                                className={styles.copyAllButton}
                            >
                                {copiedField === 'all' ? '✓ Copied All' : 'Copy All Credentials'}
                            </button>
                            <button type="button" onClick={handleCloseSuccessModal} className={styles.doneButton}>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default UserCreateForm
